import { Children, cloneElement, isValidElement, useRef, useState, useLayoutEffect, useCallback, useMemo } from 'react';
import { ClassNamed } from '../types';
import { ArrowLeftContainer, ArrowRightContainer, SlideAnimatorElement } from './style';
import { TabProps } from './Tab';
import { useTabsKeyboardNavigation } from './useTabsKeyboardNavigation';
import { TAB_DATA_ATTRIBUTES, TAB_SELECTORS } from './constants';

export interface TabsProps<T = string> extends ClassNamed, Pick<TabProps, 'underline' | 'inline'> {
    activeTabValue?: T;
    children: Array<React.ReactElement<TabProps>> | React.ReactElement<TabProps>;
    inline?: boolean;
    onTabClick?: (event: React.MouseEvent<HTMLElement>, value: T) => void;
    showDivider?: boolean;
}

export const Tabs = <T extends string = string>({
    activeTabValue,
    inline,
    underline = true,
    children,
    onTabClick,
    className,
    showDivider = false,
    ...other
}: TabsProps<T>) => {
    const ref = useRef<HTMLOListElement>(null);
    // Track the active index of the tab in the DOM, needed for router-like scenarios where the active tab is not controlled by the parent
    const [domActiveIndex, setDomActiveIndex] = useState<number>(-1);

    const checkDOMActiveElement = useCallback(() => {
        if (activeTabValue || !ref.current) return;

        const domActiveTab = ref.current.querySelector(TAB_SELECTORS.ACTIVE_TAB);
        if (!domActiveTab) {
            setDomActiveIndex(-1);
            return;
        }

        const allTabs = ref.current.querySelectorAll(`[${TAB_DATA_ATTRIBUTES.TAB_VALUE}]`);
        const newIndex = Array.from(allTabs).indexOf(domActiveTab);

        if (newIndex !== domActiveIndex) {
            setDomActiveIndex(newIndex);
        }
    }, [activeTabValue, domActiveIndex]);

    const scrollActiveTabIntoView = useCallback(
        (element?: Element) => {
            requestAnimationFrame(() => {
                const targetElement = element || ref.current?.querySelector(TAB_SELECTORS.ACTIVE_TAB);
                targetElement?.scrollIntoView({
                    inline: 'center',
                    block: 'nearest', // Avoids vertical scroll shifts
                    behavior: 'smooth',
                });
            });
        },
        [ref]
    );

    // Sync DOM-derived active state on mount and when activeTabValue changes
    useLayoutEffect(() => {
        checkDOMActiveElement();
    }, [activeTabValue, checkDOMActiveElement]);

    useLayoutEffect(() => {
        scrollActiveTabIntoView();
    }, [scrollActiveTabIntoView]);

    // Calculate effective active index for keyboard navigation
    const effectiveActiveIndex = useMemo(() => {
        return activeTabValue
            ? Children.toArray(children).findIndex(
                  (child) => isValidElement<TabProps>(child) && child.props.value === activeTabValue
              )
            : domActiveIndex;
    }, [activeTabValue, children, domActiveIndex]);

    const { handleKeyDown, focusableTabIndex } = useTabsKeyboardNavigation({
        children,
        ref,
        activeTabIndex: effectiveActiveIndex >= 0 ? effectiveActiveIndex : 0,
    });

    const handleTabClick = (event: React.MouseEvent<HTMLElement>, value: string) => {
        onTabClick?.(event, value as T);

        requestAnimationFrame(() => {
            checkDOMActiveElement();
            scrollActiveTabIntoView();
        });
    };

    const slideAnimatorProps = {
        selectedChildId: activeTabValue,
        alignToNearestOnSwipeEnd: true,
        className,
        role: 'tablist' as const,
        showArrows: true,
        arrowLeftContainer: ArrowLeftContainer,
        arrowRightContainer: ArrowRightContainer,
        showDivider: inline && showDivider,
        onKeyDown: handleKeyDown,
    };

    return (
        <SlideAnimatorElement ref={ref} {...slideAnimatorProps}>
            {Children.map(children, (child, index) => {
                if (!isValidElement<TabProps>(child)) {
                    return child;
                }

                const itemValue = child.props.value || '';

                const isActive = activeTabValue ? itemValue === activeTabValue : index === domActiveIndex;

                const isDisabled = child.props.disabled || false;
                const isFocusable = index === focusableTabIndex;

                return cloneElement(child, {
                    ...other,
                    ...child.props,
                    key: child.props.id,
                    inline,
                    active: isActive,
                    underline,
                    onClick: handleTabClick,
                    showDivider,
                    'aria-selected': isActive,
                    tabIndex: isFocusable && !isDisabled ? 0 : -1,
                    [TAB_DATA_ATTRIBUTES.TAB_VALUE]: itemValue,
                });
            })}
        </SlideAnimatorElement>
    );
};
